import {
  Box,
  Button,
  Card,
  CardBody,
  Container,
  Heading,
  HStack,
  Input,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMemo } from "react"
import {
  WareHouseService,
  type WarehouseMeldunekSearchHitDTO,
  type WarehouseProductSearchHitDTO,
} from "../../../../client"

export const Route = createFileRoute("/_layout/WMS/search/global-search")({
  validateSearch: (search: Record<string, unknown>) => ({
    q: typeof search.q === "string" ? search.q : "",
  }),
  component: GlobalSearchPage,
})

function GlobalSearchPage() {
  const navigate = useNavigate()
  const { q } = Route.useSearch()
  const query = q.trim()
  const cardBg = useColorModeValue("white", "gray.800")
  const muted = useColorModeValue("gray.600", "gray.300")

  const { data, isFetching, isError, error } = useQuery({
    queryKey: ["warehouse", "global-search", query],
    queryFn: () => WareHouseService.searchWarehouse(query),
    enabled: query.trim().length > 0,
  })

  const products = useMemo<WarehouseProductSearchHitDTO[]>(
    () => data?.products ?? [],
    [data],
  )
  const meldunki = useMemo<WarehouseMeldunekSearchHitDTO[]>(
    () => data?.meldunki ?? [],
    [data],
  )

  const openProduct = (productId: string) => {
    if (!productId) return
    navigate({
      to: "/WMS/products/$product",
      params: { product: productId },
    })
  }

  return (
    <Container maxW="6xl">
      <Stack spacing={6}>
        <Box pt={4}>
          <Heading size="lg">Wyszukiwanie globalne</Heading>
          <Text color={muted} mt={2}>
            Skanuj lub wpisz numer meldunku, SKU, EAN albo fragment nazwy
            produktu.
          </Text>
        </Box>

        <HStack align="stretch">
          <Input
            isReadOnly
            value={q}
            placeholder="Użyj wyszukiwarki w górnej części ekranu"
          />
        </HStack>

        {!query ? (
          <Text color={muted}>
            Wprowadź kod z etykiety lub dane produktu, aby znaleźć towar.
          </Text>
        ) : null}

        {isError ? (
          <Text color="red.500">
            {(error as Error)?.message ?? "Nie udało się wykonać wyszukiwania."}
          </Text>
        ) : null}

        {query &&
        !isFetching &&
        meldunki.length === 0 &&
        products.length === 0 ? (
          <Text color={muted}>Brak wyników dla: {query}</Text>
        ) : null}

        {meldunki.length > 0 ? (
          <Stack spacing={3}>
            <Heading size="md">Meldunki</Heading>
            {meldunki.map((item) => (
              <Card key={item.id} bg={cardBg}>
                <CardBody>
                  <HStack justify="space-between" align="start">
                    <Box>
                      <Text fontWeight="bold">{item.number}</Text>
                      <Text color={muted}>
                        {item.product_sku} - {item.product_name}
                      </Text>
                      <Text color={muted}>
                        EAN: {item.product_ean || "—"} | Ilość:{" "}
                        {item.produced_qty}
                      </Text>
                    </Box>
                    <Button
                      variant="outline"
                      onClick={() => openProduct(item.product_id)}
                    >
                      Otwórz towar
                    </Button>
                  </HStack>
                </CardBody>
              </Card>
            ))}
          </Stack>
        ) : null}

        {products.length > 0 ? (
          <Stack spacing={3}>
            <Heading size="md">Produkty</Heading>
            {products.map((item) => (
              <Card key={item.id} bg={cardBg}>
                <CardBody>
                  <HStack justify="space-between" align="start">
                    <Box>
                      <Text fontWeight="bold">
                        {item.sku} - {item.name}
                      </Text>
                      <Text color={muted}>
                        EAN: {item.ean || "—"} | Producent:{" "}
                        {item.manufacturer || "—"}
                      </Text>
                    </Box>
                    <Button
                      variant="outline"
                      onClick={() => openProduct(item.id)}
                    >
                      Otwórz towar
                    </Button>
                  </HStack>
                </CardBody>
              </Card>
            ))}
          </Stack>
        ) : null}
      </Stack>
    </Container>
  )
}
